import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Play, ArrowUpRight } from 'lucide-react';
import { loadYouTubeAPI, youtubeError } from '@/lib/youtubePlayer';
import { mediaPath } from '@/data/tlb/catalogue';
import waters from '@/data/tlb/waters.json';

export const FilmPlayer = forwardRef(function FilmPlayer(_, controllerRef) {
  const mountRef = useRef(null), playerRef = useRef(null), readyRef = useRef(false), requestRef = useRef(0);
  const playIntentRef = useRef(false);
  const fatalErrorRef = useRef(false);
  const [activated, setActivated] = useState(false);
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');
  const [requested, setRequested] = useState(0);
  const [currentTime, setCurrentTime] = useState(null);
  const playAt = useCallback((seconds = 0) => {
    requestRef.current = seconds; setRequested(seconds);
    if (!fatalErrorRef.current) setMessage('');
    playIntentRef.current = true;
    setActivated(true);
    if (readyRef.current && playerRef.current) {
      playerRef.current.seekTo(seconds, true); playerRef.current.playVideo();
    }
    document.getElementById('watch')?.scrollIntoView({ behavior: 'instant', block: 'start' });
  }, []);
  useImperativeHandle(controllerRef, () => ({ playAt }), [playAt]);

  useEffect(() => {
    if (!activated) return undefined;
    let disposed = false, ticker, readyTimeout;
    setStatus('loading');
    loadYouTubeAPI().then(YT => {
      if (disposed || !mountRef.current) return;
      const host = document.createElement('div'); mountRef.current.replaceChildren(host);
      readyTimeout = setTimeout(() => {
        if (!disposed && !readyRef.current) { setStatus('blocked'); setMessage('YouTube is taking longer to respond. The direct film and chapter links remain available below.'); }
      }, 15000);
      playerRef.current = new YT.Player(host, {
        width: '100%', height: '100%', videoId: waters.youtube_id,
        playerVars: { autoplay: 0, controls: 1, fs: 1, rel: 0, playsinline: 1, cc_lang_pref: 'es', cc_load_policy: 1, origin: window.location.origin },
        events: {
          onReady: event => {
            if (disposed) return;
            clearTimeout(readyTimeout); readyRef.current = true; setStatus('ready'); setMessage('');
            const iframe = event.target.getIframe();
            iframe.title = 'The Waters We Share, YouTube player';
            iframe.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
            iframe.setAttribute('allow', 'autoplay; fullscreen; encrypted-media; picture-in-picture');
            iframe.allowFullscreen = true;
            iframe.setAttribute('data-testid', 'waters-youtube-iframe');
            event.target.cueVideoById({ videoId: waters.youtube_id, startSeconds: requestRef.current });
            event.target.playVideo();
            ticker = setInterval(() => {
              if (disposed || !readyRef.current) return;
              const observedTime = event.target.getCurrentTime?.();
              if (Number.isFinite(observedTime)) setCurrentTime(observedTime);
            }, 500);
          },
          onStateChange: event => {
            if (disposed) return;
            if (fatalErrorRef.current && event.data !== 1) return;
            const states = { '-1': 'ready', 0: 'ended', 1: 'playing', 2: 'paused', 3: 'buffering', 5: 'cued' };
            setStatus(states[event.data] || 'ready');
            if (event.data === 5 && playIntentRef.current) {
              playIntentRef.current = false;
              event.target.seekTo(requestRef.current, true);
              event.target.playVideo();
            }
            if (event.data === 1) { fatalErrorRef.current = false; playIntentRef.current = false; setMessage(''); }
          },
          onAutoplayBlocked: () => { if (!disposed) { playIntentRef.current = false; setStatus('blocked'); setMessage('Your browser is waiting for another play action. Use the native YouTube Play control, or open the film below.'); } },
          onError: event => { if (!disposed) { fatalErrorRef.current = true; playIntentRef.current = false; setStatus('blocked'); setMessage(`${youtubeError(event.data)} YouTube error ${event.data}. Use the direct film or chapter link below.`); } },
        },
      });
      const iframe = playerRef.current.getIframe();
      iframe.title = 'The Waters We Share, YouTube player';
      iframe.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
      iframe.setAttribute('allow', 'autoplay; fullscreen; encrypted-media; picture-in-picture');
      iframe.allowFullscreen = true;
      iframe.setAttribute('data-testid', 'waters-youtube-iframe');
    }).catch(error => { if (!disposed) { fatalErrorRef.current = true; setStatus('blocked'); setMessage(error.message); } });
    return () => {
      disposed = true; clearInterval(ticker); clearTimeout(readyTimeout); readyRef.current = false;
      try { playerRef.current?.pauseVideo?.(); playerRef.current?.destroy?.(); } catch (_) { /* The iframe can already be detached on navigation. */ }
      playerRef.current = null;
    };
  }, [activated]);
  const fallback = seconds => `https://www.youtube.com/watch?v=${waters.youtube_id}&t=${seconds}s`;
  const activeChapter = currentTime === null ? -1 : waters.chapters.findLastIndex(chapter => currentTime >= chapter.time);

  return <section id="watch" className="tlb-player" aria-label="Watch The Waters We Share" data-testid="waters-player" data-state={status} data-current-time={currentTime ?? ''}>
    <div className="tlb-player-topline"><h2>Watch the film</h2><span className="tlb-mono">03:45 · Original score</span></div>
    <div className="tlb-video-stage">
      {!activated && <><img src={mediaPath('whale-feeding.webp')} width="1200" height="675" loading="lazy" alt="Authored 3D blue whale feeding on krill, film poster" /><button className="tlb-play-cover" type="button" onClick={() => playAt(0)} data-testid="waters-play"><span className="tlb-play-mark"><Play aria-hidden="true" /></span><span>Play the film<small>YouTube · Sound on play</small></span></button></>}
      <div ref={mountRef} className="tlb-youtube-mount" hidden={!activated} />
    </div>
    <p className="tlb-disclosure" data-testid="player-poster-disclosure">Authored 3D illustration · not the tracked whale</p>
    <div className="tlb-player-under"><p className="tlb-mono">English on screen · Spanish subtitles via YouTube CC</p><a href={fallback(requested)} target="_blank" rel="noopener" data-testid="waters-youtube-fallback">Watch on YouTube<ArrowUpRight aria-hidden="true" /></a></div>
    {message && <p className="tlb-playback-message" role="status" data-testid="waters-player-message">{message}</p>}
    <ol className="tlb-chapters" aria-label="Film chapters">{waters.chapters.map((chapter, index) => <li key={chapter.time} data-active={activeChapter === index}>
      <button type="button" onClick={() => playAt(chapter.time)} aria-current={activeChapter === index ? 'true' : undefined} data-testid={`chapter-seek-${chapter.time}`}><span className="tlb-mono">{chapter.stamp}</span><span>{chapter.title}</span></button>
      <a href={fallback(chapter.time)} target="_blank" rel="noopener" data-testid={`chapter-youtube-${chapter.time}`} aria-label={`${chapter.title}, ${chapter.stamp}, on YouTube`}>YouTube<ArrowUpRight aria-hidden="true" /></a>
    </li>)}</ol>
  </section>;
});